// Shared scheduling policy (frontend mirror): only explicit activity groups run in parallel.
const isTime = t => /^([01]\d|2[0-3]):[0-5]\d$/.test(String(t || ''));
const toMin = t => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
const toTime = m => `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const anchored = e => Boolean(e.locked || (e.manualTime ?? isTime(e.start)));
const parallelPair = (a,b) => a.kind === 'activity' && b.kind === 'activity' && Boolean(a.parallelGroupId) && a.parallelGroupId === b.parallelGroupId;
// Crew Call is an attendance window, not a department resource reservation.
// Other preparation overlaps still require an explicit group; do not infer cast availability.
const preparationTypes = new Set(['HAIR_MAKEUP','MAKEUP','WARDROBE','CAMERA_SETUP','LIGHTING','LIGHTING_SETUP']);
const callPreparationPair = (a,b) => a.kind==='activity' && b.kind==='activity' &&
  ((a.activityType==='CREW_CALL' && preparationTypes.has(b.activityType)) || (b.activityType==='CREW_CALL' && preparationTypes.has(a.activityType)));
const parallelErrors = entries => {
  const groups = new Map();
  for (const e of entries) if (e.parallelGroupId) {
    if (e.kind !== 'activity') return ['Only activities may run in parallel.'];
    if (!groups.has(e.parallelGroupId)) groups.set(e.parallelGroupId, []);
    groups.get(e.parallelGroupId).push(e);
  }
  return [...groups.values()].some(g => g.length < 2) ? ['A parallel group needs at least two activities on the same day.'] : [];
};
const cleanParallel = entries => {
  const counts = new Map();
  entries.forEach(e => { if(e.parallelGroupId) counts.set(e.parallelGroupId,(counts.get(e.parallelGroupId)||0)+1); });
  return entries.map(e => { if(e.parallelGroupId && counts.get(e.parallelGroupId)<2) {const n={...e};delete n.parallelGroupId;return n;} return e; });
};
const retime = (entries, start = '07:00') => {
  let cursor = toMin(isTime(start) ? start : '07:00'); const groups = new Map();
  return entries.map(e => {
    const group=e.kind==='activity'&&e.parallelGroupId;
    let at;
    if(group && groups.has(group)) at=groups.get(group);
    else {
      at=anchored(e)&&isTime(e.start)?toMin(e.start):cursor;
      if(group) {
        // A fixed member anchors the group, but is never itself modified.
        const fixed=entries.find(x=>parallelPair(e,x)&&anchored(x)&&isTime(x.start));
        if(fixed) at=toMin(fixed.start);
        groups.set(group,at);
      }
    }
    if(anchored(e)&&isTime(e.start)) at=toMin(e.start);
    if(anchored(e)&&!isTime(e.start)) return {...e};
    const finish=group?Math.max(...entries.filter(x=>parallelPair(e,x)).map(x=>(anchored(x)&&isTime(x.start)?toMin(x.start):at)+(x.durationMin||0))):at+(e.durationMin||0);
    cursor=groups.size || entries.indexOf(e)>0 ? Math.max(cursor,finish) : finish;
    return anchored(e) ? {...e} : {...e,start:toTime(at),...(e.manualTime===undefined&&!isTime(e.start)?{manualTime:false}:{})};
  });
};
const spans = entries => {
  let cursor = null; let previousStart = null; let offset = 0; const groups=new Map();
  return entries.map(e => {
    let at = isTime(e.start) ? toMin(e.start) + offset : cursor ?? 0;
    if(e.parallelGroupId && groups.has(e.parallelGroupId) && !anchored(e)) at=groups.get(e.parallelGroupId);
    // Clock rollover is independent of locks/manual starts. Compare starts, not finishes:
    // a long allowance must not add another day to the next overlapping entry.
    else if(previousStart!==null&&previousStart-at>720) {offset+=1440;at+=1440;}
    if(e.parallelGroupId&&!groups.has(e.parallelGroupId)) groups.set(e.parallelGroupId,at);
    previousStart=at;
    const to=at+(e.durationMin||0);cursor=Math.max(cursor??to,to);
    return {e,from:at,to};
  });
};
const dayBudget = entries => {
  const list=spans(entries); const total=entries.reduce((n,e)=>n+(e.durationMin||0),0);
  let occupied=0,end=null;
  for(const s of [...list].sort((a,b)=>a.from-b.from)) {occupied+=Math.max(0,s.to-Math.max(s.from,end??s.from));end=Math.max(end??s.to,s.to);}
  const shooting=entries.filter(e=>e.kind==='scene').reduce((n,e)=>n+(e.durationMin||0),0);
  return {total,shooting,activities:total-shooting,occupied,elapsed:list.length?Math.max(...list.map(s=>s.to))-Math.min(...list.map(s=>s.from)):0,spans:list};
};
const conflicts = entries => {
  const list=spans(entries),out=[];
   for(let i=0;i<list.length;i++)for(let j=i+1;j<list.length;j++){const a=list[i],b=list[j];if(a.from<b.to&&b.from<a.to&&!callPreparationPair(a.e,b.e)&&(!parallelPair(a.e,b.e)||a.from!==b.from))out.push({ids:[a.e.id,b.e.id],message:`"${a.e.name||'Entry'}" overlaps "${b.e.name||'Entry'}".`});}
  return out;
};
const unknownTravel = e => e.activityType==='COMPANY_MOVE' && (e.travelMin===null || (e.travelMin===undefined && /Travel time not entered/i.test(e.notes||'')));
const allowanceLabel = notes => /^Editable production planning allowance\.?$/.test(notes||'') ? 'Time allowance' : notes;
export const timeline = {anchored,parallelPair,parallelErrors,cleanParallel,retime,spans,dayBudget,conflicts,unknownTravel,allowanceLabel};

export {anchored,parallelPair,parallelErrors,cleanParallel,retime,spans,dayBudget,conflicts,unknownTravel,allowanceLabel};
